<template>
    <div class="sub-header" id="subHeader">
        <div class="sub-header__content" v-if="!isMobile">
            <DynamicLink :path="logoPath" class="sub-header__logo">
                <img src="/shared-img/header/sazkabet-logo.svg" alt="Logo SazkaBetu" width="122.2" height="52" />
            </DynamicLink>
            <nav class="sub-header__nav" aria-label="secondary">
                <ul class="sub-header__links" ref="subHeaderLinks">
                    <li
                        v-for="(item, index) in subHeaderItems"
                        :key="`${index}-${item.name}`"
                        class="sub-header__link-wrapper"
                        :class="{ 'sub-active': subMenuActive[index] }"
                        @mouseenter="handleMouseEnter(index)"
                        @mouseleave="handleMouseLeave(index)"
                        @click="handleClick(index)"
                    >
                        <DynamicLink
                            class="sub-header__link"
                            :path="item?.items.length ? null : item.linkUrl"
                            :data-text="item.name"
                            :force-active="isPrematch(item.name)"
                            @click="analytics.sendPageCategoryNavigation(item.name, 'kurzovesazky.topMenu')"
                            :blank="item.blank"
                            :is-external="item.isExternal"
                        >
                            {{ item.name }}
                            <SvgIcon v-if="item?.items.length" name="dropdown-down" />
                        </DynamicLink>
                        <div v-if="item?.items.length" class="sub-header__sub-links-wrapper">
                            <ul class="sub-header__sub-links">
                                <li v-if="item.linkUrl">
                                    <DynamicLink
                                        class="sub-header__sub-link"
                                        :path="item.linkUrl"
                                        :blank="item.blank"
                                        :is-external="item.isExternal"
                                    >
                                        Všechny {{ item.name.toLowerCase() }}
                                    </DynamicLink>
                                </li>
                                <li
                                    v-for="(subItem, subItemIndex) in item.items"
                                    :key="`${index}-${subItemIndex}-${subItem.name}`"
                                >
                                    <DynamicLink
                                        @click="
                                            analytics.sendPageCategoryNavigation(subItem.name, 'kurzovesazky.topMenu')
                                        "
                                        class="sub-header__sub-link"
                                        :path="subItem.linkUrl"
                                        :blank="subItem.blank"
                                        :is-external="subItem.isExternal"
                                    >
                                        {{ subItem.name }}
                                    </DynamicLink>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
                <OngoingContests v-if="isMdLarger" />
            </nav>
            <DarkmodeSwitch />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, Ref, ref, useTemplateRef } from "vue";
import DynamicLink from "@/shared/components/atoms/DynamicLink.vue";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import OngoingContests from "@/header/components/molecules/OngoingContests.vue";
import DarkmodeSwitch from "@/header/components/atoms/DarkmodeSwitch.vue";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import { useRoute } from "vue-router";
import { MenuItem } from "@/api/interfaces/menu";
import analytics from "@/shared/analytics";
import { debounce } from "lodash-es";

const props = defineProps<{
    items: MenuItem[];
}>();

const { isMobile, isMdLarger } = useIsBreakpoint();

const route = useRoute();

const isPrematch = (linkName: string) => {
    return (
        linkName === "Kurzy" &&
        ["Homepage", "Prematch", "PrematchSport", "PrematchLeagueDetail", "PrematchEventDetail"].some(
            (routeName) => route.name === routeName,
        )
    );
};

const logoPath = environment.sportBetsRootUrl;

const subHeaderLinks: Ref<HTMLUListElement> = useTemplateRef("subHeaderLinks");

const subMenuActive = ref<boolean[]>([]);

const getItemWidth = (item: MenuItem) => {
    const horizontalPadding = 2 * 16;
    const nameLetterCount = item.name.length;

    const letterApproximateWidth = 8.3125;

    return nameLetterCount * letterApproximateWidth + horizontalPadding;
};

const subHeaderItems = ref<MenuItem[]>([]);

const updateMenuItems = () => {
    const availableWidth = subHeaderLinks.value?.offsetWidth;

    const visibleItems: MenuItem[] = [];
    const otherItems: MenuItem = {
        name: "Další",
        linkUrl: null,
        items: [],
        image: null,
        svg: null,
        blank: false,
        isExternal: false,
    };
    let reservedSpace = 0;

    for (const item of props.items) {
        const itemWidth = getItemWidth(item);

        if (availableWidth > reservedSpace + itemWidth) {
            visibleItems.push(item);
            reservedSpace += itemWidth;
        } else {
            otherItems.items.push(item);
        }
    }

    if (otherItems.items.length > 0) {
        const lastVisibleItem = visibleItems.pop();
        if (lastVisibleItem) {
            otherItems.items.unshift(lastVisibleItem);
        }
        visibleItems.push(otherItems);
    }

    subHeaderItems.value = visibleItems;
};

const handleMouseEnter = (index: number) => {
    if (supportsHover()) {
        subMenuActive.value[index] = true;
    }
};

const handleMouseLeave = (index: number) => {
    if (supportsHover()) {
        subMenuActive.value[index] = false;
    }
};

const handleClick = (index: number) => {
    subMenuActive.value = Array(subHeaderItems.value.length).fill(false);
    subMenuActive.value[index] = true;
};

const debounceUpdate = debounce(updateMenuItems, 200);

const handleResize = () => {
    subHeaderItems.value = [];
    debounceUpdate();
};

const supportsHover = () => {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
};

onMounted(() => {
    updateMenuItems();
    window.addEventListener("resize", handleResize, { passive: true });
});

onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
});
</script>
